import React, { useState, useEffect } from 'react';
import { Tabs, Tab, Box, Typography, Button, Alert } from '@mui/material';
import { Settings } from '@mui/icons-material';
import { Helmet } from "react-helmet";
import { useNavigate } from 'react-router-dom';
import SessionConstants from '../../config/SessionConstants.js';
import BreadCrumbs from '../BreadCrumbs';
import PatientTable from '../PatientTable/index.js';
import ProgramVariantsTable from './ProgramVariantsTable/index.js';
import APIUtils from '../../utils/APIUtils';
import Loading from '../Loading';
import TabPanel from '../../uiComponents/MuiTabs/MuiTabPanel.js';
import muiTabsStyles from '../../uiComponents/MuiTabs/MuiTabsStyles.js';
import { loadViewState, saveViewState, VIEW_SCOPE } from '../../utils/ProgramSummaryViewState.js';
import './index.css';

const ProgramDashboard = () => {
  const classes = muiTabsStyles();
  const navigate = useNavigate();

  const [programId, setProgramId] = useState('');
  const [breadCrumbs, setBreadCrumbs] = useState([]);
  const [interpretationsData, setInterpretationsData] = useState([]);
  const [patientsData, setPatientsData] = useState([]);
  const [programInfo, setProgramInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  // Restore the tab the user last viewed before drilling in; defaults to Identified Variants.
  const [selectedTab, setSelectedTab] = useState(() => {
    const saved = loadViewState(VIEW_SCOPE.programDashboard, sessionStorage.getItem(SessionConstants.PROGRAM_ID));
    return typeof saved.selectedTab === 'number' ? saved.selectedTab : 0;
  });

  useEffect(() => {
    const fetchData = async () => {
      console.log('--ProgramDashboard.componentDidMount()');

      try {
        const programId = sessionStorage.getItem(SessionConstants.PROGRAM_ID);
        const variantResponse = await APIUtils.getVariantsByProgramId(programId);
        const patientResponse = await APIUtils.getPatientsByProgramId(programId);

        setProgramId(programId);
        setInterpretationsData(variantResponse);
        setPatientsData(patientResponse);
        setBreadCrumbs([{ name: 'ProgramDashboard' }]);
      } catch (error) {
        // Keep the user on the page and surface the failure inline; only a fully
        // unreachable API (handled globally in APIUtils) sends them to the Error page.
        console.error('Error loading Program Dashboard:', error);
        setLoadError('Error loading Program Dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    saveViewState(VIEW_SCOPE.programDashboard, sessionStorage.getItem(SessionConstants.PROGRAM_ID), { selectedTab: newValue });
  };

  return (
    <div className="managerSettings pageMinHeight">
      <Loading isLoading={loading} />
      <Helmet>
        <title>ED3N - Program Dashboard</title>
      </Helmet>
      <BreadCrumbs breadCrumbs={breadCrumbs} />
      <h1 className="pageTitle">Program Dashboard</h1>
      {loadError && !loading && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setLoadError('')}>
          {loadError}
        </Alert>
      )}
      <div className="managerButtonBar">
        <div className="managerLeftName">NBS Program: {programId}</div>
        <div className="managerButtons">
          <Button
            className="settingsButton"
            onClick={() => navigate('/programsettings')}
            startIcon={<Settings />}
            variant="contained"
            color="primary"
          >
            Program Settings
          </Button>
        </div>
      </div>
      <div>
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
          className={classes.tabs} // Apply muiTabStyles to Tabs container
        >
          <Tab label="Identified Variants" id="tab-0" aria-controls="tabpanel-0" className={classes.tab} />
          <Tab label="Patients for Review" id="tab-1" aria-controls="tabpanel-1" className={classes.tab} />
        </Tabs>
        <TabPanel value={selectedTab} index={0}>
          {interpretationsData.length ? (
            <ProgramVariantsTable
              data={interpretationsData}
              programId={programId}
              breadCrumbs={breadCrumbs}
              viewScope={VIEW_SCOPE.programDashboard}
            />
          ) : (
            <div className="noData">No Variant Data</div>
          )}
        </TabPanel>
        <TabPanel value={selectedTab} index={1}>
          {patientsData.length > 0 ? (
            <PatientTable
              data={patientsData}
              programId={programId}
              breadCrumbs={breadCrumbs}
              viewScope={VIEW_SCOPE.programDashboard}
            />
          ) : (
            <div className="noData">No Patient Data</div>
          )}
        </TabPanel>
      </div>
    </div>
  );
};

export default ProgramDashboard;
